/**
 * Lọc nhiễu bằng median + MAD — Prompt.md §11.
 *
 * Không dùng mean/σ: trong 5 giây thu mẫu, trẻ gần như luôn ngọ nguậy 1–2 lần với biên độ lớn.
 * Chính outlier đó kéo mean lệch **và** làm σ phình ra, nên ngưỡng "3σ" rộng đến mức không loại
 * được gì. Median/MAD miễn nhiễm tới 50% dữ liệu nhiễu, và baseline sai là sai vĩnh viễn trong
 * cả phiên học vì §11 quy định mọi so sánh phía sau đều tương đối với nó.
 */

import type { CalibrationConfig } from "@studystraight/shared-config";
import type { MeasurementKey, Metric } from "@studystraight/shared-types";
import { median, medianAbsoluteDeviation } from "@studystraight/vision-core";

export interface DimensionSummary {
  key: MeasurementKey;
  /** `availabilityRatio` đạt `minMetricAvailabilityRatio` **và** có ít nhất một sample. */
  available: boolean;
  /** Tỉ lệ sample mà chiều này `available` (DU #3 — hông bị bàn che). */
  availabilityRatio: number;
  /** Trung vị của các sample được giữ. `NaN` khi chiều này không đo được. */
  center: number;
  /** MAD của các sample được giữ — thước đo "trẻ có ngồi yên không". `NaN` nếu không đo được. */
  spread: number;
  /** Số sample còn lại sau khi lọc outlier. */
  kept: number;
  /** Số sample `available` trước khi lọc outlier. */
  observed: number;
  /** Tổng số sample được đưa vào (kể cả sample không `available`). */
  total: number;
}

/**
 * Tổng hợp một chiều đo từ chuỗi sample của cả đợt calibration.
 *
 * MAD = 0 (mọi giá trị bằng nhau, hoặc quá ít mẫu) được xử lý riêng: khi đó `|v - center| <= 0`
 * sẽ loại sạch mọi điểm lệch dù chỉ lệch 1e-9, làm baseline mất phần lớn dữ liệu vì lý do vô nghĩa.
 */
export function summarizeDimension(
  key: MeasurementKey,
  metrics: readonly Metric[],
  config: CalibrationConfig,
): DimensionSummary {
  const total = metrics.length;
  const values = metrics.filter((m) => m.available && Number.isFinite(m.value)).map((m) => m.value);

  const availabilityRatio = total === 0 ? 0 : values.length / total;
  const availableEnough =
    values.length > 0 && availabilityRatio >= config.minMetricAvailabilityRatio;

  if (values.length === 0) {
    return {
      key,
      available: false,
      availabilityRatio,
      center: Number.NaN,
      spread: Number.NaN,
      kept: 0,
      observed: 0,
      total,
    };
  }

  const roughCenter = median(values);
  const roughSpread = medianAbsoluteDeviation(values, roughCenter);
  const limit = config.outlierMadFactor * roughSpread;
  const kept = roughSpread > 0 ? values.filter((v) => Math.abs(v - roughCenter) <= limit) : values;

  const center = median(kept);
  return {
    key,
    available: availableEnough,
    availabilityRatio,
    center,
    spread: medianAbsoluteDeviation(kept, center),
    kept: kept.length,
    observed: values.length,
    total,
  };
}
